import { useState } from 'react';
import { useAuthStore } from '../auth/store';
import { getApiErrorMessage } from '../../lib/api-client';
import { useReverseApSettlement } from '../accounting/api';
import { useSupplierApSettlements } from './api';
import { SettlementListPanel } from './SettlementListPanel';

/** 正在等確認的解除沖銷(REQ-20261029002)。確認列畫在表格上方,理由同 CustomerArPanel */
interface PendingReverse {
  id: number;
  documentNo: string;
}

export function SupplierApPanel({ supplierId }: { supplierId: number }): JSX.Element {
  const [page, setPage] = useState(0);
  const q = useSupplierApSettlements(supplierId, page);
  /**
   * 解除沖銷限 ADMIN(REQ-20261029002,照抄應收側 REQ-20260821001)。
   * 前端只是不給入口 —— 後端 `POST /ap-settlements/:id/reverse` 掛 `@Roles('ADMIN')`。
   */
  const isAdmin = useAuthStore((s) => s.user?.role === 'ADMIN');
  const [pending, setPending] = useState<PendingReverse | null>(null);
  const [reason, setReason] = useState('');
  const [flash, setFlash] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const reverseMutation = useReverseApSettlement(supplierId);

  const rows = (q.data?.items ?? []).map((i) => ({
    id: i.id,
    documentNo: i.documentNo,
    subCode: i.subCode,
    settledAt: i.settledAt,
    legacyPartyCode: i.legacyPartyCode,
    cashAmount: i.cashAmount,
    checkAmount: i.checkAmount,
    usePrepaid: i.usePrepaid,
    totalSettled: i.totalSettled,
    balanceAfter: i.payableAfter,
    prepaidAfter: i.prepaidAfter,
    source: i.source,
    lineCount: i.lineCount,
    reversalOfId: i.reversalOfId,
    reversedByDocumentNo: i.reversedByDocumentNo,
  }));

  const startReverse = (row: { id: number; documentNo: string }): void => {
    setPending({ id: row.id, documentNo: row.documentNo });
    setReason('');
    setFlash(null);
    setActionError(null);
  };

  const confirmReverse = (target: PendingReverse): void => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setActionError('請填寫解除沖銷的原因');
      return;
    }
    reverseMutation.mutate(
      { settlementId: target.id, reason: trimmed },
      {
        onSuccess: (created) => {
          setPending(null);
          setReason('');
          setFlash(
            `${target.documentNo} 已解除沖銷(反向單 ${created.documentNo})。這筆付款沖過的進貨單已扣回已付金額,未結明細會重新計算。`,
          );
        },
        onError: (err) => setActionError(getApiErrorMessage(err, '解除沖銷失敗')),
      },
    );
  };

  return (
    <div className="grid gap-2">
      {pending ? (
        <div
          className="flex flex-wrap items-center justify-end gap-2 bg-amber-50 border border-amber-200 rounded px-3 py-2 text-sm"
          data-testid="settlement-reverse-confirm"
        >
          <label className="mr-auto flex items-center gap-2 text-slate-700">
            解除 {pending.documentNo} 的原因
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="border rounded-md px-2 py-1 text-sm"
              data-testid="settlement-reverse-reason"
            />
          </label>
          <button
            type="button"
            disabled={reverseMutation.isPending}
            onClick={() => confirmReverse(pending)}
            className="text-red-600 hover:underline disabled:opacity-50"
          >
            確定解除
          </button>
          <button
            type="button"
            onClick={() => {
              setPending(null);
              setActionError(null);
            }}
            className="text-slate-600 hover:underline"
          >
            取消
          </button>
        </div>
      ) : null}

      {flash ? (
        <div
          role="status"
          className="text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2 text-sm"
        >
          {flash}
        </div>
      ) : null}

      {actionError ? (
        <div
          role="alert"
          className="text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 text-sm"
          data-testid="settlement-reverse-error"
        >
          {actionError}
        </div>
      ) : null}

      <SettlementListPanel
        side="ap"
        loading={q.isLoading}
        error={q.isError ? '載入應付沖帳失敗' : null}
        rows={rows}
        total={q.data?.total ?? 0}
        opening={{
          receivableOrPayable: q.data?.openingPayable ?? null,
          prepaid: q.data?.openingPrepaid ?? null,
        }}
        totalSettledSum={q.data?.totalSettledSum ?? '0'}
        page={page}
        onPageChange={setPage}
        {...(isAdmin ? { onReverse: startReverse } : {})}
      />
    </div>
  );
}
